import { adminApi } from './clients';

/**
 * Re-enrolling a family by hand.
 *
 * The quote goes through the server's own fee engine — the same one the parent screens post to — so
 * nothing here works out a fee or applies a discount. If it did, the printed form would eventually
 * disagree with what the family is charged.
 */

export const lookupFamily = async (parentCode) => {
    const { data } = await adminApi.get('/manual-reenrolment/family', {
        params: { parent_code: parentCode },
    });
    return data || {};
};

export const quoteFees = async (body) => {
    const { data } = await adminApi.post('/manual-reenrolment/quote', body);
    return data || {};
};

export const createManualEntry = async (body) => {
    const { data } = await adminApi.post('/manual-reenrolment', body);
    return data || {};
};

export const recordPayment = async ({ SessionID, paymentKind, amount }) => {
    const { data } = await adminApi.post('/manual-reenrolment/payment', { SessionID, paymentKind, amount });
    return data || {};
};

/**
 * Approves a manual entry once its signed form is in. The same approval as the re-enrolments list,
 * so the server records who made it and the bot can then take it to TASS. Either team may make it,
 * whoever uploaded the form included.
 */
export const approveEntry = async (SessionID) => {
    const { data } = await adminApi.post('/approveParentRecord', { SessionID });
    return data;
};

export const markReceipted = async (SessionID) => {
    const { data } = await adminApi.post('/manual-reenrolment/receipted', { SessionID });
    return data || {};
};

export const fetchNotes = async (SessionID) => {
    const { data } = await adminApi.get('/manual-reenrolment/notes', { params: { SessionID } });
    return data?.notes || [];
};

export const addNote = async ({ SessionID, note }) => {
    const { data } = await adminApi.post('/manual-reenrolment/notes', { SessionID, note });
    return data?.notes || [];
};

/**
 * The pre-filled Confirmation of Continued Enrolment, saved to the computer for printing.
 *
 * Fetched through the signed-in client rather than opened as a link, because the endpoint needs the
 * admin's token and a plain link would not carry it.
 */
export const downloadConfirmationForm = async (SessionID) => {
    const { data } = await adminApi.get('/manual-reenrolment/confirmation-form', {
        params: { SessionID },
        responseType: 'blob',
    });
    saveFile(data, `Confirmation of Continued Enrolment - ${SessionID}.pdf`);
};

/** The same form with nothing filled in but the campus, for AR to complete by hand. */
export const downloadBlankForm = async (college) => {
    const { data } = await adminApi.get('/manual-reenrolment/blank-form', {
        params: { college },
        responseType: 'blob',
    });
    saveFile(data, `Confirmation of Continued Enrolment - ${college} - blank.pdf`);
};

const saveFile = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

/** Every manual entry and where it is; with cancelled, the cancelled ones as well. */
export const fetchManualEntries = async ({ cancelled = false } = {}) => {
    const { data } = await adminApi.get('/manual-reenrolment/entries', {
        params: cancelled ? { cancelled: 1 } : {},
    });
    return { entries: data?.entries || [], cancelled: data?.cancelled || [] };
};

/** Cancels a manual entry. The reason is kept with a snapshot of it. */
export const cancelEntry = async ({ SessionID, reason }) => {
    const { data } = await adminApi.post('/manual-reenrolment/cancel', { SessionID, reason });
    return data || {};
};

/**
 * The signed form and what was paid today. Completes the entry, which then waits for approval in
 * the re-enrolments list. The amount is recorded for the reports only; AR receipt it in TASS.
 */
export const finishEntry = async ({ SessionID, file, paymentKind, amount }) => {
    const body = new FormData();
    body.append('SessionID', SessionID);
    body.append('paymentKind', paymentKind);
    body.append('amount', amount);
    body.append('signedForm', file);
    const { data } = await adminApi.post('/manual-reenrolment/finish', body);
    return data || {};
};

/**
 * A blob error body is still JSON underneath; read it so the screen can say what the server said.
 */
export const errorFrom = async (requestError, fallback) => {
    const body = requestError?.response?.data;
    if (body instanceof Blob) {
        try {
            return JSON.parse(await body.text())?.error || fallback;
        } catch {
            return fallback;
        }
    }
    return body?.error || fallback;
};
